/**
 * Utilities for recovering from S3 pre-signed URL expiry during report polling.
 *
 * When a report takes longer to generate than the lifetime of the initial
 * pre-signed URL, S3 returns 403 (InvalidToken / AccessDenied).  We can
 * recover by re-signing the same S3 object key through the `REPORT_DOWNLOAD`
 * action of the `getPreSignedUrl` AppSync mutation.
 */
import { API } from "src/shared/lib/amplify"
import { GET_PRE_SIGNED_URL } from "src/graphql/queries/common"

/**
 * Extract the S3 object key from a pre-signed URL.
 *
 * Handles the two URL styles produced by AWS SDKs:
 *   Virtual-hosted: https://bucket.s3.amazonaws.com/{key}?…
 *   Path-style:     https://s3.amazonaws.com/{bucket}/{key}?…
 *
 * Returns null if the URL cannot be parsed.
 */
export const extractS3KeyFromUrl = (presignedUrl: string): string | null => {
  try {
    const url = new URL(presignedUrl)
    const hostname = url.hostname

    // Virtual-hosted style – bucket is encoded in the sub-domain.
    if (/\.s3[.-]/.test(hostname) || hostname.endsWith(".s3.amazonaws.com")) {
      // pathname is /{key} (possibly URL-encoded)
      return decodeURIComponent(url.pathname.slice(1)) || null
    }

    // Path-style: hostname is s3[.region].amazonaws.com, pathname is /{bucket}/{key}
    const pathParts = url.pathname.slice(1).split("/")
    if (pathParts.length < 2) return null
    return decodeURIComponent(pathParts.slice(1).join("/")) || null
  } catch {
    return null
  }
}

/**
 * Obtain a fresh pre-signed URL for an S3 object whose existing pre-signed
 * URL has expired (HTTP 403 InvalidToken / AccessDenied).
 *
 * @param expiredUrl  The expired pre-signed URL returned by the report mutation.
 * @param buildingId  The building context used by the Lambda to locate the bucket.
 *                    Pass an empty string for facility-wide reports.
 * @returns A new pre-signed URL string, or null if re-signing fails.
 */
export const resignReportUrl = async (
  expiredUrl: string,
  buildingId: string
): Promise<string | null> => {
  try {
    const key = extractS3KeyFromUrl(expiredUrl)
    if (!key) return null

    const input = JSON.stringify({
      key,
      buildingId,
      action: "REPORT_DOWNLOAD"
    })

    const result: any = await API.graphql({
      query: GET_PRE_SIGNED_URL,
      variables: { input }
    })

    const freshUrl = JSON.parse(result?.data?.getPreSignedUrl)?.body
    return freshUrl || null
  } catch {
    return null
  }
}
